import { useState, useEffect, useRef } from "react";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Image as ImageIcon, 
  Save, 
  X,
  MapPin,
  Star,
  Upload,
  ExternalLink,
  Camera
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "bathroom", label: "Bathroom" },
  { value: "kitchen", label: "Kitchen" },
  { value: "floor", label: "Floor Tiling" },
  { value: "outdoor", label: "Outdoor" },
  { value: "commercial", label: "Commercial" }
];

const PortfolioManager = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "bathroom",
    location: "",
    featured: false,
    images: []
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/owner/portfolio`, { headers });
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      title: "",
      description: "",
      category: "bathroom",
      location: "",
      featured: false,
      images: []
    });
    setIsCreating(true);
    setEditingProject(null);
  };

  const handleEdit = (project) => {
    setFormData({
      title: project.title || "",
      description: project.description || "",
      category: project.category || "bathroom",
      location: project.location || "",
      featured: project.featured || false,
      images: project.images || []
    });
    setEditingProject(project);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setEditingProject(null);
    setIsCreating(false);
    setFormData({
      title: "",
      description: "",
      category: "bathroom",
      location: "",
      featured: false,
      images: []
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Project title is required");
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        await axios.post(`${API}/owner/portfolio`, formData, { headers });
        toast.success("Project created");
      } else if (editingProject) {
        await axios.put(`${API}/owner/portfolio/${editingProject.id}`, formData, { headers });
        toast.success("Project updated");
      }
      handleCancel();
      fetchProjects();
    } catch (error) {
      toast.error("Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    
    try {
      await axios.delete(`${API}/owner/portfolio/${projectId}`, { headers });
      toast.success("Project deleted");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }

      try {
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const response = await axios.post(`${API}/owner/portfolio/upload`, {
              image_data: reader.result,
              filename: file.name,
              project_id: editingProject?.id || null
            }, { headers });
            
            // Add the image URL to form data
            setFormData(prev => ({
              ...prev,
              images: [...prev.images, response.data.image_url]
            }));
            
            toast.success(`${file.name} uploaded`);
          } catch (error) {
            toast.error(`Failed to upload ${file.name}`);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        toast.error(`Failed to read ${file.name}`);
      }
    }
    
    setUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Edit/Create Form
  if (isCreating || editingProject) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="portfolio-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {isCreating ? "Add New Project" : "Edit Project"}
          </h2>
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Project Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Modern Bathroom Renovation"
              className="mt-1"
              data-testid="project-title-input"
            />
          </div>

          <div>
            <Label>Description</Label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the project..."
              className="mt-1 w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background text-sm"
              data-testid="project-description-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="project-category-select"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g. Manchester"
                className="mt-1"
                data-testid="project-location-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.featured}
              onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              data-testid="project-featured-checkbox"
            />
            <Label className="cursor-pointer">Featured Project (shown on homepage)</Label>
          </div>

          {/* Images */}
          <div>
            <Label>Project Images</Label>
            <div className="mt-2 space-y-2">
              {formData.images.map((url, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <img 
                    src={url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} 
                    alt="" 
                    className="w-16 h-16 object-cover rounded" 
                    onError={(e) => e.target.style.display = 'none'} 
                  />
                  <span className="flex-1 text-sm truncate">{url}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeImage(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
            
            {/* Upload Button */}
            <div className="mt-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
                data-testid="image-upload-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full"
                data-testid="upload-photo-btn"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" />
                    Upload Photos
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-1 text-center">
                Click to select photos from your device (mobile, tablet, or desktop)
              </p>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={saving} data-testid="save-project-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Project"}
            </Button>
            <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  // Project List
  return (
    <div data-testid="portfolio-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Portfolio Projects</h2>
          <p className="text-sm text-gray-500">{projects.length} projects</p>
        </div>
        <Button onClick={handleCreate} data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No portfolio projects yet</p>
          <Button className="mt-4" onClick={handleCreate}>Add Your First Project</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div 
              key={project.id} 
              className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`project-card-${project.id}`}
            >
              {project.images && project.images[0] ? (
                <img 
                  src={project.images[0]} 
                  alt={project.title}
                  className="w-full h-40 object-cover"
                  onError={(e) => e.target.src = '/placeholder-project.jpg'}
                />
              ) : (
                <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-gray-300" />
                </div>
              )}
              
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 line-clamp-1">{project.title}</h3>
                  {project.featured && (
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <span className="px-2 py-0.5 bg-primary/10 text-primary rounded">
                    {CATEGORIES.find(c => c.value === project.category)?.label || project.category}
                  </span>
                  {project.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {project.location}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {project.description || "No description"}
                </p>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(project)}>
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(project.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortfolioManager;
